import React, { useState, useEffect, useRef, memo } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import websocketService from "../../../function/websocketService";
import { getToken } from "../../../function/token";

const WebSocketNotification = memo(() => {
  const [notifications, setNotifications] = useState([]);
  const notifiedIdsRef = useRef(new Set());

  useEffect(() => {
    const handleWebSocketMessage = (eventType, data) => {
      if (eventType !== 'message') return;

      try {
        let parsedData = typeof data === 'string' ? JSON.parse(data) : data;

        if (parsedData.type !== 'message' || parsedData.data?.type !== 'incoming') return;

        if (parsedData.data) parsedData = parsedData.data;

        const senderId = parsedData.from || parsedData.wa_id || parsedData.user_id || parsedData.sender;
        const toId = parsedData.to;
        const messageId = parsedData.id || parsedData.message_id;

        if (!messageId) return;

        // Deduplicate notifications by message ID
        if (notifiedIdsRef.current.has(messageId)) return;
        notifiedIdsRef.current.add(messageId);

        // Get current user info for deduplication
        const { currentUserWaId, currentUserPhone } = getToken();

        if (
          (senderId && currentUserWaId && String(senderId).trim() === String(currentUserWaId).trim()) ||
          (senderId && currentUserPhone && String(senderId).includes(String(currentUserPhone))) ||
          (toId && currentUserPhone && String(toId).includes(String(currentUserPhone)))
        ) return;

        const notification = {
          id: Date.now(),
          wa_id: parsedData.wa_id,
          from: parsedData.from,
          text: parsedData.text || parsedData.body || 'New message received',
          timestamp: new Date(),
          type: 'message',
        };

        setNotifications(prev => [notification, ...prev.slice(0, 4)]);

        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }, 10000);
      } catch (error) {
        // Silently ignore malformed WebSocket data
      }
    };

    const unsubscribe = websocketService.onMessage(handleWebSocketMessage);
    return unsubscribe;
  }, []);

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <div
      className="position-fixed"
      style={{ top: '20px', right: '20px', zIndex: 9999, maxWidth: '350px' }}
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {notifications.map(notification => (
        <div
          key={notification.id}
          className="alert alert-dismissible fade show mb-2"
          role="alert"
          style={{
            backgroundColor: '#25D366',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideInRight 0.25s ease',
          }}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faBell} className="me-2" />
            <div className="flex-grow-1">
              <div className="fw-bold" style={{ fontSize: '14px' }}>New Message</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {notification.text.length > 50
                  ? `${notification.text.substring(0, 50)}...`
                  : notification.text}
              </div>
              <div style={{ fontSize: '11px', opacity: 0.7 }}>
                {notification.timestamp.toLocaleTimeString()}
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              onClick={() => removeNotification(notification.id)}
              aria-label="Close notification"
            />
          </div>
        </div>
      ))}
    </div>
  );
});

export default WebSocketNotification;